import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import axios from 'axios';
import { FileText, Eye, Search, PlusCircle, ChevronDown, ChevronUp, X, ArrowUp, ArrowDown } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const OrderListVes = ({ onNavigate }) => {
  const { user } = useContext(UserContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'work_permit_number', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  console.log("requests", requests)
  const fetchWorkPermits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/workpermit/?status=pen`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching work permits:', error);
      setRequests([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkPermits();
  }, []);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const sortedData = getSortedRequests();
    const calculatedTotalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, requests.length, searchTerm, currentPage]);

  const updateWorkPermitStatus = async (
    requestId,
    currentStatus,
    editedStartDate,
    editedEndDate,
    teamId = null
  ) => {
    try {
      if (currentStatus !== 'pending') {
        alert('Status update not allowed for this status.');
        return;
      }

      const payload = {
        status: 'signed',
        planned_date_start: editedStartDate,
        planned_date_end: editedEndDate,
      };

      if (teamId) {
        payload.team = teamId;
      }

      const response = await axios.patch(
        `${API_BASE_URL}/workpermit/${requestId}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('Work permit updated successfully:', response.data);
        setShowDetails(false);
        await fetchWorkPermits(); // Odśwież dane po aktualizacji
      }
    } catch (error) {
      console.error('Error updating work permit:', error);
      alert('Failed to update work permit.');
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sortRequests = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const getSortedRequests = () => {
    if (!requests) return [];

    let filteredRequests = [...requests];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredRequests = filteredRequests.filter((request) => {
        const matchesUser = request.team_data?.users?.some((user) =>
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(term)
        );
        return (
          request.work_permit_number?.toLowerCase().includes(term) ||
          request.order_number?.toLowerCase().includes(term) ||
          request.turbine_data?.turbine_number?.toLowerCase().includes(term) ||
          request.turbine_data?.location?.toLowerCase().includes(term) ||
          request.company_name?.toLowerCase().includes(term) ||
          request.work_scope_data?.name?.toLowerCase().includes(term) ||
          matchesUser
        );
      });
    }

    filteredRequests.sort((a, b) => {
      if (sortConfig.key === 'work_permit_number') {
        const valA = a.work_permit_number ?? '';
        const valB = b.work_permit_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'order_number') {
        const valA = a.order_number ?? '';
        const valB = b.order_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'turbine_data.turbine_number') {
        const valA = a.turbine_data?.turbine_number ?? '';
        const valB = b.turbine_data?.turbine_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'planned_date_start') {
        const valA = a.planned_date_start ? new Date(a.planned_date_start) : new Date(0);
        const valB = b.planned_date_start ? new Date(b.planned_date_start) : new Date(0);
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      } else if (sortConfig.key === 'work_scope_data.name') {
        const valA = a.work_scope_data?.name ?? '';
        const valB = b.work_scope_data?.name ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return 0;
    });

    return filteredRequests;
  };

  const handleNavigateToRequestForm = () => {
    onNavigate('request-work');
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const handleSendClick = (requestId) => {
    onNavigate('request-work', null, { workPermitId: requestId });
  };

  const handleRowDoubleClick = (request) => {
    handleSendClick(request.id);
  };

  const handleViewDocuments = (request) => {
    console.log('Viewing documents for order:', request.work_permit_number);
    if (!request.attachments) {
      console.log('No attachments found for this request');
      alert('No attachments available for this request.');
      return;
    }
    if (typeof request.attachments === 'string') {
      window.open(request.attachments, '_blank');
    } else if (Array.isArray(request.attachments) && request.attachments.length > 0) {
      window.open(request.attachments[0], '_blank');
    } else {
      console.log('Invalid attachments format:', request.attachments);
      alert('Invalid or empty attachments.');
    }
  };

  const filteredRequests = getSortedRequests();
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const RequestDetailsModal = () => {
    const [showTeamMembers, setShowTeamMembers] = useState(true);
    const [userList, setUserList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editedStartDate, setEditedStartDate] = useState(selectedRequest?.planned_date_start || '');
    const [editedEndDate, setEditedEndDate] = useState(selectedRequest?.planned_date_end || '');
    const [editedTeam, setEditedTeam] = useState(selectedRequest?.team_data?.users || []);
    const [teamLeaderId, setTeamLeaderId] = useState(selectedRequest?.team_data?.team_leader || null);
    const [preparerId, setPreparerId] = useState(
      selectedRequest?.team_data?.person_responsible_for_preparation_of_work_place || null
    );
    const [teamIds, setTeamIds] = useState(editedTeam.map((member) => member.id));

    useEffect(() => {
      const fetchUsers = async () => {
        try {
          if (isEditing && userList.length === 0) {
            const response = await fetch(`${API_BASE_URL}/users/minimal/`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              },
            });
            const data = await response.json();
            console.log("Fetched users data:", data);
            setUserList(data);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          alert('Failed to fetch user list.');
        }
      };

      fetchUsers();
    }, [isEditing, userList.length]);

    useEffect(() => {
      console.log('Updated team:', editedTeam);
    }, [editedTeam]);

    if (!showDetails || !selectedRequest) return null;

    const isSupervisor = selectedRequest.team_data?.team_leader === user?.id;

    const handleChangeLeader = (newLeaderId) => {
      const newLeader = userList.find((user) => user.id === newLeaderId);
      if (newLeader) {
        const updatedTeam = editedTeam.filter((member) => member.id !== teamLeaderId);
        if (!updatedTeam.some((member) => member.id === newLeaderId)) {
          updatedTeam.push(newLeader);
        }
        setTeamLeaderId(newLeaderId);
        setEditedTeam(updatedTeam);
        setTeamIds(updatedTeam.map((member) => member.id));
      }
    };

    const handlePreparerChange = (newPreparerId) => {
      if (newPreparerId === 0) {
        if (preparerId) {
          setEditedTeam(editedTeam.filter((member) => member.id !== preparerId));
          setTeamIds(teamIds.filter((id) => id !== preparerId));
        }
        setPreparerId(null);
        return;
      }

      if (preparerId && preparerId !== newPreparerId) {
        setEditedTeam(editedTeam.filter((member) => member.id !== preparerId));
        setTeamIds(teamIds.filter((id) => id !== preparerId));
      }

      if (!teamIds.includes(newPreparerId)) {
        const newPreparer = userList.find((user) => user.id === newPreparerId);
        if (newPreparer) {
          setEditedTeam([...editedTeam, newPreparer]);
          setTeamIds([...teamIds, newPreparerId]);
        }
      }

      setPreparerId(newPreparerId);
    };

    const handleAddMember = (userId) => {
      if (!teamIds.includes(userId)) {
        const newMember = userList.find((user) => user.id === userId);
        if (newMember) {
          setEditedTeam([...editedTeam, newMember]);
          setTeamIds([...teamIds, userId]);
        }
      }
    };

    const handleRemoveMember = (userId) => {
      if (userId === teamLeaderId) {
        alert('Cannot remove the supervisor. Change the supervisor first.');
        return;
      }

      const updatedTeam = editedTeam.filter((member) => member.id !== userId);
      const updatedTeamIds = teamIds.filter((id) => id !== userId);

      if (userId === preparerId) {
        setPreparerId(null);
      }

      setEditedTeam(updatedTeam);
      setTeamIds(updatedTeamIds);
    };

    const isTeamEdited = () => {
      const initialTeamIds = selectedRequest.team_data.users.map((user) => user.id).sort((a, b) => a - b);
      console.log('initialTeamIds:', initialTeamIds);
      const currentTeamIds = [...teamIds].sort((a, b) => a - b);
      console.log('currentTeamIds:', currentTeamIds);
      return JSON.stringify(initialTeamIds) !== JSON.stringify(currentTeamIds);
    };

    const handleSave = async () => {
      const oldTeamName = selectedRequest?.team_data?.name || 'UnnamedTeam';
      const newTeamName = `${oldTeamName}_copy`;

      const teamData = {
        name: newTeamName,
        team_leader: teamLeaderId,
        person_responsible_for_preparation_of_work_place: preparerId,
        team: teamIds,
      };

      try {
        let newTeamId = null;

        if (isTeamEdited()) {
          console.log('Saving team data:', teamData);
          const response = await fetch(`${API_BASE_URL}/teams/`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(teamData),
          });

          console.log('Server response status:', response.status);
          if (!response.ok) {
            const errorData = await response.json();
            console.error('Server returned an error:', errorData);
            throw new Error(`Failed to save team data: ${errorData.detail || 'Unknown error'}`);
          }

          const data = await response.json();
          console.log('Team saved successfully:', data);
          newTeamId = data.id;
        }

        await updateWorkPermitStatus(
          selectedRequest.id,
          selectedRequest.status,
          editedStartDate,
          editedEndDate,
          newTeamId
        );

        alert('Data saved successfully!');
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving data:', error);
        alert('Failed to save data.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#002D43]">
                Order Details: {selectedRequest.work_permit_number}
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="p-1.5 bg-gray-100 text-[#002D43] rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="border rounded-md overflow-hidden">
                <button
                  onClick={() => setShowTeamMembers(!showTeamMembers)}
                  className="w-full px-4 py-2 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
                >
                  <span className="text-sm font-medium text-[#002D43]">
                    Team Members ({editedTeam.length})
                  </span>
                  {showTeamMembers ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                </button>

                {showTeamMembers && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-500">Supervisor:</span>
                        {isEditing ? (
                          <select
                            value={teamLeaderId}
                            onChange={(e) => {
                              const newLeaderId = parseInt(e.target.value);
                              handleChangeLeader(newLeaderId);
                            }}
                            className="text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          >
                            {userList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm font-medium text-gray-800">
                            {editedTeam.find((member) => member.id === teamLeaderId)?.first_name || ''}{' '}
                            {editedTeam.find((member) => member.id === teamLeaderId)?.last_name || ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-500">Preparer:</span>
                        {isEditing ? (
                          <select
                            value={preparerId || ''}
                            onChange={(e) => handlePreparerChange(parseInt(e.target.value))}
                            className="text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          >
                            <option value="0">None</option>
                            {userList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm font-medium text-gray-800">
                            {editedTeam.find((member) => member.id === preparerId)?.first_name || ''}{' '}
                            {editedTeam.find((member) => member.id === preparerId)?.last_name || 'None'}
                          </span>
                        )}
                      </div>
                      {isEditing && preparerId && (
                        <button
                          onClick={() => handlePreparerChange(0)}
                          className="text-red-500 hover:text-red-600 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {editedTeam
                      .filter((member) => member.id !== teamLeaderId && member.id !== preparerId)
                      .map((member) => (
                        <div key={member.id} className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-500">Member:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                          {isEditing && (
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              className="text-red-500 hover:text-red-600 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}

                    {isEditing && (
                      <div className="flex items-center space-x-2">
                        <select
                          onChange={(e) => handleAddMember(parseInt(e.target.value))}
                          className="text-sm border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          defaultValue=""
                        >
                          <option value="" disabled>
                            Add Member
                          </option>
                          {userList
                            .filter((user) => !editedTeam.some((member) => member.id === user.id))
                            .map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Work permit number</label>
                  <p className="mt-1 text-gray-800">{selectedRequest.work_permit_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Order number</label>
                  <p className="mt-1 text-gray-800">{selectedRequest.order_number}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Turbine Information</label>
                  <p className="mt-1 text-gray-800">
                    Turbine: {selectedRequest.turbine_data.turbine_number}
                    <br />
                    {selectedRequest.turbine_data.type_data.name} - {selectedRequest.turbine_data.model_data.name}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Location</label>
                  <p className="mt-1 text-gray-800">{selectedRequest.turbine_data.location}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Start</label>
                  {isEditing ? (
                    <input
                      type="datetime-local"
                      value={editedStartDate}
                      onChange={(e) => setEditedStartDate(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  ) : (
                    <p className="mt-1 text-gray-800">{formatDateTime(selectedRequest.planned_date_start)}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">End</label>
                  {isEditing ? (
                    <input
                      type="datetime-local"
                      value={editedEndDate}
                      onChange={(e) => setEditedEndDate(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  ) : (
                    <p className="mt-1 text-gray-800">{formatDateTime(selectedRequest.planned_date_end)}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Work Scope</label>
                  <p className="mt-1 text-gray-800 whitespace-pre-line">{selectedRequest.work_scope_data.name}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="px-4 py-2 bg-white border font-semibold text-black rounded-full hover:bg-gray-100"
                  >
                    Close
                  </button>
                  {isSupervisor && selectedRequest.status === 'pending' && (
                    <button
                      className="px-4 py-2 bg-white border font-semibold text-black rounded-full hover:bg-gray-100"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </button>
                  )}
                  {isSupervisor && selectedRequest.status === 'pending' && (
                    <button
                      onClick={() =>
                        updateWorkPermitStatus(
                          selectedRequest.id,
                          selectedRequest.status
                        )
                      }
                      className="px-4 py-2 bg-green-500 border font-semibold text-black rounded-full hover:bg-green-600"
                    >
                      Send
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-white border font-semibold text-black rounded-full hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-green-500 border font-semibold text-black rounded-full hover:bg-green-600"
                  >
                    Save & Send
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header Section */}
      <div className="flex-shrink-0 p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="text-2xl font-bold text-[#002D43]">Orders List</h1>          
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 mx-4 mb-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-full flex flex-col">
        {/* Search */}
        <div className="p-4 flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="relative flex-1 w-full">
            <input
              placeholder="Search by order number, turbine, location..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>
        </div>
        </div>

        {/* Table container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[#012D43] min-w-[120px]">
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => sortRequests('order_number')}
                    >
                      Order Number
                      {renderSortIndicator('order_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-[#012D43] min-w-[120px]">
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => sortRequests('work_permit_number')}
                    >
                      Work Permit Number
                      {renderSortIndicator('work_permit_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#012D43] min-w-[140px]">
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => sortRequests('turbine_data.turbine_number')}
                    >
                      Turbine Info
                      {renderSortIndicator('turbine_data.turbine_number')}
                    </button>
                  </th>
                  { /*<th className="px-4 py-3 text-left text-xs font-medium text-[#012D43] min-w-[160px]">
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => sortRequests('planned_date_start')}
                    >
                      Schedule
                      {renderSortIndicator('planned_date_start')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#012D43] min-w-[140px]">
                    Supervisor
                  </th> */ }
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#012D43] min-w-[140px]">
                    <button
                      className="flex items-center gap-1 hover:text-gray-700"
                      onClick={() => sortRequests('work_scope_data.name')}
                    >
                      Work Scope
                      {renderSortIndicator('work_scope_data.name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#012D43] w-[60px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-2 text-center text-gray-500">
                      Loading work permits...
                    </td>
                  </tr>
                ) : paginatedRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-2 text-center text-gray-500">
                      No work permits available.
                    </td>
                  </tr>
                ) : (
                  paginatedRequests.map((request) => (
                    <tr
                      key={request.id}
                      className="hover:bg-gray-50"
                      onDoubleClick={() => handleRowDoubleClick(request)}
                    >
                      <td className="px-4 py-2 text-sm font-medium text-[#002D43] truncate max-w-[200px]">
                        {request.order_number}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-[#002D43] truncate max-w-[200px]">
                        {request.work_permit_number}
                        <div className="text-sm text-gray-400 truncate max-w-[200px]">{request.turbine_data.location}</div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-[200px]">
                        {request.turbine_data.turbine_number}
                        <div className="text-sm text-gray-400 truncate max-w-[200px]">
                          {request.turbine_data.model_data.name} - {request.turbine_data.type_data.name}
                        </div>
                      </td>
                      { /* <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-[200px]">
                        <div className="text-sm text-gray-500">No data</div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-[200px]">
                        {(() => {
                          const teamLeader = request.team_data?.users?.find(
                            (user) => user.id === request.team_data.team_leader
                          );
                          return teamLeader ? (
                            <>
                              <div className="truncate max-w-[200px]">{teamLeader.first_name} {teamLeader.last_name}</div>
                              <div className="text-sm text-gray-400 truncate max-w-[200px]">{teamLeader.email}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">No Supervisor</span>
                          );
                        })()}
                      </td> */ }
                      <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-[200px]">
                        {request.work_scope_data.name}
                      </td>
                      <td className="px-4 py-2 text-sm text-right w-[60px]">
                        <div className="flex justify-end gap-2">
                          <button
                            className="px-4 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-full hover:bg-opacity-90 transition-colors"
                            onClick={() => handleSendClick(request.id)}
                            title="Fill in & Sign"
                          >
                            CreateWP
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 px-4 pb-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-[#002D43]">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredRequests.length)} of {filteredRequests.length} items
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm px-4 py-2 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black min-w-[140px]"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex space-x-2">
              {currentPage > 1 && (
                <button
                  className="px-4 py-2 border border-gray-300 text-[#002D43] rounded-full hover:bg-gray-50 flex items-center justify-center"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left h-4 w-4 mr-1"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  Previous
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 border ${
                    page === currentPage
                      ? 'border-gray-300 bg-[#E5F7FF] text-black'
                      : 'border-gray-300 text-[#002D43]'
                  } rounded-full hover:bg-gray-50 flex items-center justify-center`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-4 py-2 border border-gray-300 text-[#002D43] rounded-full hover:bg-gray-50 flex items-center justify-center"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right h-4 w-4 ml-1"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <RequestDetailsModal />
    </div>
  );
};

export default OrderListVes;
